import React, { useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Paper, FormControlLabel, Switch, Button } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { History } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuditLogPanelProps } from './AuditLogPanel.types';
import useAuditLog from './useAuditLog';
import { isNewFormat, processRecords, filterOnHiddenFields } from './AuditLogPanel.utils';
import AuditLogEntry from './AuditLogEntry';

const useStyles = makeStyles((theme: Theme) => ({
	wrapper: {
		marginLeft: 24,
		marginRight: 24
	},
	paper: {
		borderRadius: 16,
		overflow: 'hidden',
		background: theme.palette.background.paper
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		height: 64,
		padding: '0 16px',
		borderBottom: `1px solid ${theme.palette.divider}`,
		gap: 12
	},
	title: {
		margin: 0,
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		fontSize: '1.1rem',
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: 'oklch(55% 0.008 55)'
	},
	toggleLabel: {
		margin: 0,
		'& .MuiFormControlLabel-label': {
			fontSize: '1.2rem',
			color: 'oklch(60% 0.008 55)'
		}
	},
	timelineContainer: {
		position: 'relative',
		// Vertical timeline line
		'&::before': {
			content: '""',
			position: 'absolute',
			left: 19,
			top: 22,
			bottom: 22,
			width: 1,
			background: theme.palette.divider,
			pointerEvents: 'none'
		}
	},
	entryWrapper: {
		borderBottom: `1px solid ${theme.palette.divider}`,
		'&:last-child': {
			borderBottom: 'none'
		}
	},
	emptyState: {
		padding: '32px 16px',
		textAlign: 'center',
		fontSize: '1.4rem',
		color: 'oklch(62% 0.008 55)'
	},
	idleState: {
		padding: '32px 16px',
		display: 'flex',
		flexDirection: 'column' as const,
		alignItems: 'center',
		gap: 10
	},
	idleHint: {
		margin: 0,
		fontSize: '1.3rem',
		color: 'oklch(65% 0.007 55)'
	}
}));

const motionProps = {
	initial: { opacity: 0, y: 12 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.2, ease: [0, 0, 0.2, 1] as const }
};

function AuditLogPanel({ type, uid, config, orderBy, limit, systemActorLabel = 'System' }: AuditLogPanelProps) {
	const [activated, setActivated] = useState(false);
	const { records, users } = useAuditLog(type, uid, orderBy, limit, activated);
	const { disabledRecords, hiddenFields } = config;
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [seeUnchangedData, setSeeUnchangedData] = useState(false);
	const classes = useStyles();

	const hasLegacyRecords = records.some(r => !isNewFormat(r));

	const visibleRecords = processRecords(records)
		.sort((a, b) => a.changedAtRaw - b.changedAtRaw)
		.filter(r => !disabledRecords.find(d => r.action.startsWith(d)))
		.filter(r => filterOnHiddenFields(r, hiddenFields));

	return (
		<motion.div {...motionProps} className={classes.wrapper}>
			<Paper className={classes.paper} elevation={1}>
				<header className={classes.header}>
					<History style={{ fontSize: '1.8rem', color: 'oklch(63% 0.008 55)', flexShrink: 0 }} />
					<h4 className={classes.title}>Aktivitetslog</h4>
					{hasLegacyRecords && (
						<FormControlLabel
							className={classes.toggleLabel}
							label="Se uændret data"
							control={
								<Switch
									checked={seeUnchangedData}
									onChange={() => setSeeUnchangedData(b => !b)}
									color="primary"
									size="small"
								/>
							}
						/>
					)}
				</header>

				{!activated ? (
					<div className={classes.idleState}>
						<Button variant="outlined" size="small" onClick={() => setActivated(true)}>
							Hent aktivitetslog
						</Button>
						<p className={classes.idleHint}>Indlæses kun ved behov</p>
					</div>
				) : visibleRecords.length === 0 ? (
					<p className={classes.emptyState}>Ingen aktivitet</p>
				) : (
					<div className={classes.timelineContainer}>
						{visibleRecords.map(record => (
							<div key={record.id} className={classes.entryWrapper}>
								<AuditLogEntry
									record={record}
									config={config}
									users={users}
									systemActorLabel={systemActorLabel}
									seeUnchangedData={seeUnchangedData}
									expanded={expandedId === record.id}
									onToggle={() => setExpandedId(id => (id === record.id ? null : record.id))}
								/>
							</div>
						))}
					</div>
				)}
			</Paper>
		</motion.div>
	);
}

export default AuditLogPanel;
